<template>
  <b-card no-body>
    <b-card-body class="filter pb-0">
      <b-row>
        <!-- User Search -->
        <b-col
          v-if="!exclude.includes('user-search')"
          sm="12"
          :md="userSearchCol"
          class="px-0 px-md-1"
        >
          <b-form-group
            :label="$t(userSearchLabel)"
          >
            <user-search
              v-model="form.userId"
              :placeholder="userPlaceholder"
              :multiple="multipleUserSelect"
              :enable-all-users="enableAllUsers"
              :force-all-users="forceAllUsers"
              @input="$emit('input', form)"
            />
          </b-form-group>
        </b-col>

        <!-- Tabs -->
        <b-col
          v-if="tabs"
          sm="12"
          md="3"
        >
          <b-form-group>
            <template #label>
              <slot name="title">
                <span>{{ $t('Time Interval') }}</span>
              </slot>
            </template>
            <b-button-group>
              <b-button
                v-for="(tab, index) in tabs"
                :id="tab.label"
                :key="tab.label"
                :variant="selectedTab === index ? 'primary' : 'link'"
                class="rounded-lg border-0"
                style="padding: 14px;"
                @click="$emit('tab-select', index)"
              >
                {{ $t(tab.label) }}
              </b-button>
            </b-button-group>
          </b-form-group>

          <date-range-picker
            trigger-target="Date Range"
            :start-date="form.start_date"
            :end-date="form.end_date"
            @change="(({ startDate, endDate}) => {
              form.start_date = startDate
              form.end_date = endDate
              $emit('tab-select', tabs.length - 1)
              $emit('input', form)
            })"
          />
        </b-col>

        <!-- Date Range Filter -->
        <b-col
          v-if="!exclude.includes('date-range')"
          sm="12"
          :md="dateRangeCol"
          class="d-flex justify-content-between align-items-baseline px-0 px-md-1 mt-1 mt-md-0"
        >
          <template v-if="dateType === 'range'">
            <b-form-group
              id="custom-date-range"
              :label="$t('Date Range')"
              class="mx-auto cursor-pointer w-75"
            >
              <div
                class="border border-2 rounded-lg"
                style="padding: 0.75rem;"
              >
                <feather-icon
                  icon="CalendarIcon"
                  size="15"
                  class="mr-1"
                />
                {{ form.start_date ? FORMAT_DATE_RANGE(form.start_date, form.end_date) : $t('Start Date - End Date') }}
              </div>
            </b-form-group>

            <date-range-picker
              trigger-target="custom-date-range"
              :start-date="form.start_date"
              :end-date="form.end_date"
              @change="(({ startDate, endDate}) => {
                form.start_date = startDate
                form.end_date = endDate
                $emit('input', form)
              })"
            />
          </template>
          <!-- <template v-if="dateType == 'month'">
            <b-form-group :label="$t('Month Start')">
              <b-form-input
                v-model="form.month_start"
                type="month"
                onfocus="this.showPicker()"
                :min="minMonth"
                :max="form.month_end"
                :date-format-options="IS_MOBILE() ? COMMON.MOBILE_DATE_PICKER_FORMAT : COMMON.DATE_PICKER_FORMAT"
                @input="$emit('input', form)"
              />
            </b-form-group>
            <b-form-group :label="$t('Month End')">
              <b-form-input
                v-model="form.month_end"
                type="month"
                onfocus="this.showPicker()"
                class="ml-1"
                :min="form.month_start"
                :date-format-options="IS_MOBILE() ? COMMON.MOBILE_DATE_PICKER_FORMAT : COMMON.DATE_PICKER_FORMAT"
                @input="$emit('input', form)"
              />
            </b-form-group>
          </template> -->
          <template v-if="dateType === 'single'">
            <div>
              <b-button
                variant="flat-success"
                class="btn-icon p-0"
                @click="previousDate"
              >
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="25"
                />
              </b-button>
            </div>
            <b-form-group
              class="w-100"
              :label="$t('Date')"
            >
              <b-form-datepicker
                id="datepicker-full-width"
                v-model="form.date"
                :placeholder="$t('Select Date')"
                calendar-width="100%"
                :max="today"
                :date-format-options="IS_MOBILE() ? COMMON.MOBILE_DATE_PICKER_FORMAT : COMMON.DATE_PICKER_FORMAT"
                @input="$emit('input', form)"
              />
            </b-form-group>
            <div
              v-if="TODAY({ localTime: false }) !== form.date"
            >
              <b-button
                variant="flat-success"
                class="btn-icon p-0"
                @click="nextDate"
              >
                <feather-icon
                  icon="ChevronRightIcon"
                  size="25"
                />
              </b-button>
            </div>
          </template>
          <template v-else-if="dateType === 'week' || dateType === 'month'">
            <div>
              <b-button
                variant="flat-success"
                class="btn-icon p-0"
                @click="previousDate"
              >
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="25"
                />
              </b-button>
            </div>
            <b-form-group
              class="w-100"
              :label="$t('Date')"
            >
              <b-form-datepicker
                id="datepicker-full-width"
                v-model="form.date"
                :placeholder="$t('Select Date')"
                calendar-width="100%"
                :date-info-fn="highlightDates"
                :max="today"
                :date-format-options="IS_MOBILE() ? COMMON.MOBILE_DATE_PICKER_FORMAT : COMMON.DATE_PICKER_FORMAT"
                @input="$emit('input', form)"
              />
            </b-form-group>
            <div
              v-if="TODAY({ localTime: false }) !== form.date"
            >
              <b-button
                variant="flat-success"
                class="btn-icon p-0"
                @click="nextDate"
              >
                <feather-icon
                  icon="ChevronRightIcon"
                  size="25"
                />
              </b-button>
            </div>
          </template>
          <slot
            v-else
            name="disabledData"
          />
        </b-col>

        <!-- Timezone -->
        <b-col
          v-if="!exclude.includes('timezone')"
          sm="12"
          :md="timezoneCol"
          class="pb-2 px-0 px-md-1"
        >
          <b-form-group :label="$t('Timezone')">
            <v-select
              :value="form.time_zone"
              :options="timeZones"
              :reduce="item => item.value"
              label="title"
              :clearable="false"
              @input="updateTimezone"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-else
          sm="12"
          :md="timezoneCol"
          class="pb-2 px-0 px-md-1"
        >
          <slot
            name="right-side"
          />
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>
<script>
import vSelect from 'vue-select'
import {
  BCard, BCardBody, BFormDatepicker, BButton, BFormGroup, BButtonGroup,
}
from 'bootstrap-vue'
import UserSearch from '@/views/common/components/UserSearch.vue'
import useApollo from '@/plugins/graphql/useApollo'
import moment from 'moment'
import EventBus from '@/event-bus'
import DateRangePicker from './DateRangePicker.vue'

export default {
  components: {
    BCard,
    UserSearch,
    BCardBody,
    BFormDatepicker,
    BFormGroup,
    BButtonGroup,
    vSelect,
    BButton,
    DateRangePicker,
  },
  props: {
    value: { // date  userID
      type: Object,
      required: true,
    },
    exclude: {
      type: Array,
      default: () => [],
    },
    dateType: {
      type: String,
      default: '',
    },
    userPlaceholder: {
      type: String,
      default: 'Me',
    },
    multipleUserSelect: {
      type: Boolean,
      default: false,
    },
    enableAllUsers: {
      type: Boolean,
      default: false,
    },
    userSearchLabel: {
      type: String,
      default: 'Select User',
    },
    enableFilterTypeSelection: {
      type: Boolean,
      default: false,
    },
    forceAllUsers: {
      type: Boolean,
      default: false,
    },
    userSearchCol: {
      type: Number,
      default: 4,
    },
    timezoneCol: {
      type: Number,
      default: 4,
    },
    dateRangeCol: {
      type: Number,
      default: 4,
    },
    tabs: {
      type: Array,
      default: null,
    },
    selectedTab: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      form: {
        date: this.value.date,
        userId: this.value.userId,
        time_zone: this.value.time_zone,
        start_date: this.value.start_date,
        end_date: this.value.end_date,
        month_start: this.value.month_start,
        month_end: this.value.month_end,
        today: this.value.today,
      },
      timeZones: [],
      filterOptions: [
        { text: this.$t('All'), value: 'all' },
        { text: this.$t('Single'), value: 'single' },
      ],
    }
  },
  computed: {
    minMonth() {
      return moment().subtract('1', 'year').format('YYYY-MM')
    },
    today() {
      return this.TODAY({ localTime: false })
    },
  },
  watch: {
    'value.start_date': {
      handler(val) {
        this.form.start_date = val
      },
      immediate: true,
    },
    forceAllUsers: {
      handler(val) {
        if (val) {
          this.form.userId = null
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.getCompanyTimezones()
    EventBus.$on('timezones-updated', () => {
      this.getCompanyTimezones()
    })
  },
  beforeDestroy() {
    EventBus.$off('timezones-updated')
  },
  methods: {
    highlightDates(_, date) {
      if (this.dateType === 'week') {
        if (moment(date).isSameOrAfter(moment(this.form.date).startOf('week').toDate()) && moment(date).isSameOrBefore(moment(this.form.date).endOf('week').toDate())) {
          return 'table-primary'
        }
      }

      if (this.dateType === 'month') {
        return moment(date).isSameOrAfter(moment(this.form.date).startOf('month').toDate()) && moment(date).isSameOrBefore(moment(this.form.date).endOf('month').toDate()) ? 'table-primary' : ''
      }

      return ''
    },
    getCompanyTimezones() {
      useApollo.company.getMyCompanyTimeZone({ companyUid: this.$store.state.project.selectedCompany }).then(response => {
        const company = response.data.me.companies.data[0]
        const defaultTimezone = company.timezoneOffset
        const reportTimezone = company.timezones.data.filter(timezone => timezone.name !== defaultTimezone.name)
        this.timeZones = [
          {
            title: defaultTimezone.displayName,
            value: defaultTimezone.name,
            filter: defaultTimezone.filter,
          },
          ...reportTimezone.map(rt => ({ title: rt.displayName, value: rt.name, filter: rt.filter })),
        ]
        this.$store.dispatch('project/setTimezones', this.timeZones)
      }).catch(error => {
        console.log(error)
      })
    },
    previousDate() {
      if (this.dateType === 'single') {
        this.form = {
          ...this.form,
          date: moment(this.form.date).subtract(1, 'days').format('YYYY-MM-DD'),
        }
      } else if (this.dateType === 'week') {
        this.form = {
          ...this.form,
          date: moment(this.form.date).subtract(1, 'weeks').format('YYYY-MM-DD'),
        }
      } else if (this.dateType === 'month') {
        this.form = {
          ...this.form,
          date: moment(this.form.date).subtract(1, 'months').format('YYYY-MM-DD'),
        }
      }
      this.$emit('input', this.form)
    },
    nextDate() {
      if (this.dateType === 'single') {
        this.form = {
          ...this.form,
          date: moment(this.form.date).add(1, 'days').format('YYYY-MM-DD'),
        }
      } else if (this.dateType === 'week') {
        this.form = {
          ...this.form,
          date: moment(this.form.date).add(1, 'weeks').format('YYYY-MM-DD'),
        }
      } else if (this.dateType === 'month') {
        this.form = {
          ...this.form,
          date: moment(this.form.date).add(1, 'months').format('YYYY-MM-DD'),
        }
      }
      this.$emit('input', this.form)
    },
    updateTimezone(newValue) {
      this.$store.dispatch('project/STORE_SELECTED_TIMEZONE', newValue).then(() => {
        const timeZone = this.timeZones.find(zone => zone.value === newValue)
        this.$store.dispatch('project/STORE_SELECTED_TIMEZONE_FILTER', timeZone).then(() => {
          this.form = {
            ...this.form,
            time_zone: newValue,
          }
          this.$emit('input', this.form)
          this.$emit('timezone-changed')
        })
      })
    },
  },
}
</script>

<style lang="scss">
.filter {
  .vs__selected-options {
    min-height: 35px !important;
  }
  .b-form-datepicker.form-control {
    min-height: 42px !important;
    label {
      padding-right: 8px;
    }
  }
}
.timezone-select .vs__search {
  display: none;
}
</style>
